/**
 * The venue's legal identity, as the shop endpoint hands it over.
 *
 * MultiSafepay (and every other acquirer) will not let a venue sell gift cards until its
 * shop names the trading company and links reachable terms and a privacy statement. All
 * four come from `settings.business` on the location — see `shared/legal/businessIdentity`.
 */
export interface ShopLegal {
  legal_name?: string;
  registration_number?: string;
  /** What the country calls that number: "KvK", "KBO/BCE", "SIREN". */
  registration_label?: string;
  vat_number?: string;
  address_lines?: string[];
  email?: string;
  phone?: string;
  website?: string;
  /** Set only when the venue publishes its own page instead of the hosted one. */
  terms_url?: string;
  privacy_url?: string;
}

export type LegalDocumentKind = 'terms' | 'privacy';

export interface LegalLink {
  href: string;
  /** An outside page opens in a new tab; a hosted one stays in the shop. */
  external: boolean;
}

/**
 * Where a footer link should point. The venue's own page wins; otherwise the guest stays
 * in the shop and reads the document 10Seat builds from the venue's own details.
 */
export function legalDocumentLink(
  kind: LegalDocumentKind,
  locationId: string | undefined,
  legal: ShopLegal | undefined,
): LegalLink | null {
  const own = (kind === 'terms' ? legal?.terms_url : legal?.privacy_url)?.trim();
  if (own) return { href: own, external: true };
  if (!locationId) return null;
  return { href: `/shop/${encodeURIComponent(locationId)}/${kind}`, external: false };
}

/** "Eetcafé Klaar B.V. · KvK 12345678 · Btw NL0012…", skipping whatever is not filled in. */
export function legalIdentityParts(legal: ShopLegal | undefined, vatLabel: string): string[] {
  if (!legal) return [];
  const parts: string[] = [];
  if (legal.legal_name) parts.push(legal.legal_name);
  if (legal.registration_number) {
    parts.push([legal.registration_label, legal.registration_number].filter(Boolean).join(' '));
  }
  if (legal.vat_number) parts.push(`${vatLabel} ${legal.vat_number}`);
  return parts;
}
